import { AbstractConnector } from '@web3-react/abstract-connector'
import warning from 'tiny-warning'

function parseSendReturn(sendReturn) {
  // eslint-disable-next-line no-prototype-builtins
  return sendReturn.hasOwnProperty('result') ? sendReturn.result : sendReturn
}

export class NoEthereumProviderError extends Error {
  constructor() {
    super()
    this.name = this.constructor.name
    this.message = 'No Bitkeep wallet not found'
  }
}

export class UserRejectedRequestError extends Error {
  constructor() {
    super()
    this.name = this.constructor.name
    this.message = 'The user rejected the request.'
  }
}

export class BitgetConnector extends AbstractConnector {
  constructor(kwargs) {
    super(kwargs)
    this.handleNetworkChanged = this.handleNetworkChanged.bind(this)
    this.handleChainChanged = this.handleChainChanged.bind(this)
    this.handleAccountsChanged = this.handleAccountsChanged.bind(this)
    this.handleClose = this.handleClose.bind(this)
  }

  handleChainChanged(chainId) {
    this.emitUpdate({ chainId, provider: window.bitkeep.ethereum })
  }

  handleAccountsChanged(accounts) {
    if (accounts.length === 0) {
      this.emitDeactivate()
    } else {
      this.emitUpdate({ account: accounts[0] })
    }
  }

  handleClose() {
    this.emitDeactivate()
  }

  handleNetworkChanged(networkId) {
    this.emitUpdate({ chainId: networkId, provider: this.bitgetWalletProvider })
  }

  async activate() {
    if (!window.bitkeep?.ethereum) {
      throw new NoEthereumProviderError()
    } else if (!this.bitgetWalletProvider) {
      this.bitgetWalletProvider = window.bitkeep && window.bitkeep.ethereum
    }

    if (this.bitgetWalletProvider.on) {
      this.bitgetWalletProvider.on('chainChanged', this.handleChainChanged)
      this.bitgetWalletProvider.on('accountsChanged', this.handleAccountsChanged)
      this.bitgetWalletProvider.on('close', this.handleClose)
      this.bitgetWalletProvider.on('networkChanged', this.handleNetworkChanged)
    }

    // try to activate + get account via eth_requestAccounts
    let account
    try {
      account = await this.bitgetWalletProvider.send('eth_requestAccounts').then((sendReturn) => parseSendReturn(sendReturn)[0])
    } catch (error) {
      if (error.code === 4001) {
        throw new UserRejectedRequestError()
      }
      warning(false, 'eth_requestAccounts was unsuccessful, falling back to enable')
    }

    // if unsuccessful, try enable
    if (!account) {
      // if enable is successful but doesn't return accounts, fall back to getAccount (not happy i have to do this...)
      account = await this.bitgetWalletProvider.enable().then((sendReturn) => sendReturn && parseSendReturn(sendReturn)[0])
    }

    return { provider: this.bitgetWalletProvider, ...(account ? { account } : {}) }
  }

  async getProvider() {
    return this.bitgetWalletProvider
  }

  async getChainId() {
    if (!this.bitgetWalletProvider) {
      throw new NoEthereumProviderError()
    }

    let chainId
    try {
      chainId = await this.bitgetWalletProvider.send('eth_chainId').then(parseSendReturn)
    } catch {
      warning(false, 'eth_chainId was unsuccessful, falling back to net_version')
    }

    if (!chainId) {
      try {
        chainId = await this.bitgetWalletProvider.send('net_version').then(parseSendReturn)
      } catch {
        warning(false, 'net_version was unsuccessful, falling back to net version v2')
      }
    }

    if (!chainId) {
      try {
        chainId = parseSendReturn(this.bitgetWalletProvider.send({ method: 'net_version' }))
      } catch {
        warning(false, 'net_version v2 was unsuccessful, falling back to manual matches and static properties')
      }
    }

    return chainId
  }

  async getAccount() {
    if (!this.bitgetWalletProvider) {
      throw new NoEthereumProviderError()
    }

    let account
    try {
      account = await this.bitgetWalletProvider.send('eth_accounts').then((sendReturn) => parseSendReturn(sendReturn)[0])
    } catch {
      warning(false, 'eth_accounts was unsuccessful, falling back to enable')
    }

    if (!account) {
      try {
        account = await this.bitgetWalletProvider.enable().then((sendReturn) => parseSendReturn(sendReturn)[0])
      } catch {
        warning(false, 'enable was unsuccessful, falling back to eth_accounts v2')
      }
    }

    if (!account) {
      account = parseSendReturn(this.bitgetWalletProvider.send({ method: 'eth_accounts' }))[0]
    }

    return account
  }

  deactivate() {
    if (this.bitgetWalletProvider && this.bitgetWalletProvider.removeListener) {
      this.bitgetWalletProvider.removeListener('chainChanged', this.handleChainChanged)
      this.bitgetWalletProvider.removeListener('accountsChanged', this.handleAccountsChanged)
      this.bitgetWalletProvider.removeListener('close', this.handleClose)
      this.bitgetWalletProvider.removeListener('networkChanged', this.handleNetworkChanged)
      this.bitgetWalletProvider.removeAllListeners()
      this.bitgetWalletProvider.disconnect()
    }
  }

  async isAuthorized() {
    if (!this.bitgetWalletProvider) {
      return false
    }

    try {
      return await this.bitgetWalletProvider.send('eth_accounts').then((sendReturn) => {
        if (parseSendReturn(sendReturn).length > 0) {
          return true
        } else {
          return false
        }
      })
    } catch {
      return false
    }
  }
}
